import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Github, ExternalLink, Calendar, Clock, User } from 'lucide-react';
import { projects } from '../mockData';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

const ProjectDetail = () => {
  const { id } = useParams();
  const project = projects.find(p => p.id === parseInt(id));

  if (!project) {
    return <Navigate to="/projects" />;
  }

  const statusColors = {
    'Completed': 'bg-green-500/10 text-green-400 border-green-500/20',
    'In Progress': 'bg-amber-500/10 text-amber-400 border-amber-500/20'
  };

  return (
    <div className="min-h-screen bg-black text-white pt-24 pb-16 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Link to="/projects" className="inline-flex items-center gap-2 text-neutral-400 hover:text-white transition-colors mb-8">
          <ArrowLeft size={20} />
          Back to Projects
        </Link>

        {/* Header */}
        <div className="mb-12">
          <div className="flex items-start justify-between mb-4">
            <h1 className="text-4xl md:text-5xl font-bold">{project.title}</h1>
            <Badge className={statusColors[project.status]}>
              {project.status}
            </Badge>
          </div>
          <p className="text-xl text-neutral-400 mb-6">{project.subtitle}</p>

          {/* Meta */}
          <div className="flex flex-wrap gap-6 text-sm text-neutral-400 mb-6">
            <div className="flex items-center gap-2">
              <Calendar size={16} />
              {project.year}
            </div>
            <div className="flex items-center gap-2">
              <Clock size={16} />
              {project.duration}
            </div>
            <div className="flex items-center gap-2">
              <User size={16} />
              {project.role}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            {project.github && (
              <a href={project.github} target="_blank" rel="noopener noreferrer">
                <Button className="bg-neutral-900 hover:bg-neutral-800 text-white border border-neutral-700">
                  <Github size={18} className="mr-2" />
                  View Code
                </Button>
              </a>
            )}
            {project.demo && (
              <a href={project.demo} target="_blank" rel="noopener noreferrer">
                <Button className="bg-cyan-500 hover:bg-cyan-600 text-black">
                  <ExternalLink size={18} className="mr-2" />
                  Live Demo
                </Button>
              </a>
            )}
          </div>
        </div>

        {/* Project Image Placeholder */}
        <div className="aspect-video bg-gradient-to-br from-neutral-800 to-neutral-900 rounded-lg mb-12 flex items-center justify-center">
          <div className="text-8xl font-mono text-neutral-700">{project.title.charAt(0)}</div>
        </div>

        {/* Overview */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">Overview</h2>
          <p className="text-neutral-300 leading-relaxed">{project.description}</p>
        </section>

        {/* Tech Stack */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">Tech Stack</h2>
          <div className="flex flex-wrap gap-2">
            {project.techStack.map((tech, index) => (
              <span
                key={index}
                className="px-3 py-1.5 text-sm font-medium bg-neutral-900 text-neutral-300 rounded-lg border border-neutral-800"
              >
                {tech}
              </span>
            ))}
          </div>
        </section>

        {/* Problem / Solution / Impact */}
        <section className="mb-12 space-y-8">
          <div className="border-l-2 border-cyan-500 pl-6">
            <h3 className="text-xl font-semibold mb-2 text-cyan-400">Problem</h3>
            <p className="text-neutral-300 leading-relaxed">{project.problem}</p>
          </div>

          <div className="border-l-2 border-amber-500 pl-6">
            <h3 className="text-xl font-semibold mb-2 text-amber-400">Solution</h3>
            <p className="text-neutral-300 leading-relaxed">{project.solution}</p>
          </div>

          <div className="border-l-2 border-green-500 pl-6">
            <h3 className="text-xl font-semibold mb-2 text-green-400">Impact</h3>
            <p className="text-neutral-300 leading-relaxed">{project.impact}</p>
          </div>
        </section>

        {/* Features */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-4">Key Features</h2>
          <ul className="space-y-3">
            {project.features.map((feature, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="text-cyan-400 mt-1">•</span>
                <span className="text-neutral-300">{feature}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Technical Challenges */}
        {project.challenges.length > 0 && (
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-4">Technical Challenges</h2>
            <div className="space-y-6">
              {project.challenges.map((item, index) => (
                <div key={index} className="bg-neutral-900/50 border border-neutral-800 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-white mb-2">Challenge: {item.challenge}</h3>
                  <p className="text-neutral-400"><span className="text-green-400 font-medium">Solution:</span> {item.solution}</p>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ProjectDetail;
